import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import axios from 'axios';
import { resolveImageUrl } from '@/utils/image';
import InlineEnquiryForm from '@/components/InlineEnquiryForm';

const SiteFooter = lazy(() => import('@/components/SiteFooter'));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ANIMATION_OFFSET_Y = 30;
const MAX_STAGGER_DELAY = 0.3;
const STAGGER_PER_ITEM = 0.03;

const pageReveal = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };
const lightboxFade = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };
const lightboxScale = { initial: { scale: 0.9 }, animate: { scale: 1 }, transition: { duration: 0.3 } };

const GalleryGrid = ({ photos, onSelect }) => (
  <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
    {photos.map((photo, index) => (
      <motion.div
        key={photo.id}
        className="relative overflow-hidden cursor-pointer break-inside-avoid"
        initial={{ opacity: 0, y: ANIMATION_OFFSET_Y }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: Math.min(index * STAGGER_PER_ITEM, MAX_STAGGER_DELAY) }}
        viewport={{ once: true, margin: '-50px' }}
        onClick={() => onSelect(photo)}
        whileHover={{ scale: 1.02 }}
        data-testid={`gallery-photo-${index}`}
      >
        <img src={resolveImageUrl(photo.image_url)} alt={photo.title || 'Gallery photo'} className="w-full grayscale-hover" loading="lazy" decoding="async" />
        {photo.title && (
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-sm font-medium">{photo.title}</p>
          </div>
        )}
      </motion.div>
    ))}
  </div>
);

const EmptyGallery = () => (
  <div className="text-center py-20 border border-white/10 rounded-none">
    <p className="text-xl text-gray-400 mb-4">Gallery is empty</p>
    <p className="text-sm text-gray-500">Photos will appear here once added by the admin</p>
  </div>
);

const Gallery = () => {
  const [photos, setPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPhotos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/gallery`);
      setPhotos(res.data);
    } catch (err) { if (process.env.NODE_ENV === 'development') console.error('Gallery fetch failed:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const content = useMemo(() => {
    if (loading) return <div className="text-center py-20"><p className="text-gray-400">Loading gallery...</p></div>;
    if (photos.length === 0) return <EmptyGallery />;
    return <GalleryGrid photos={photos} onSelect={setSelectedPhoto} />;
  }, [loading, photos]);

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12 lg:px-24">
      <motion.div {...pageReveal}>
        <h1 className="text-6xl md:text-8xl font-medium tracking-tight leading-none mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>Gallery</h1>
        <div className="flex items-center justify-between mb-16">
          <p className="text-sm tracking-widest uppercase text-gray-400">A collection of captured moments</p>
          <p className="text-sm tracking-widest uppercase text-gray-400">Click to expand</p>
        </div>
        {content}
      </motion.div>
      <AnimatePresence>
        {selectedPhoto && (
          <motion.div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-6" {...lightboxFade} onClick={() => setSelectedPhoto(null)} data-testid="lightbox">
            <button className="absolute top-6 right-6 text-white hover:text-gray-400 transition-colors" onClick={() => setSelectedPhoto(null)} data-testid="lightbox-close"><X size={32} /></button>
            <motion.img src={resolveImageUrl(selectedPhoto.image_url)} alt={selectedPhoto.title || 'Gallery photo'} className="max-w-full max-h-full object-contain" {...lightboxScale} />
          </motion.div>
        )}
      </AnimatePresence>
      <InlineEnquiryForm heading="Like What You See?" subheading="Get in touch to discuss your shoot." />
      <Suspense fallback={null}><SiteFooter /></Suspense>
    </div>
  );
};

export default Gallery;
